import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

import type { Image, Category } from '../../services/api';
import api from '../../services/api';

import BulkActionToolbar from './BulkActionToolbar';
import ImageEditModal from './ImageEditModal';
import ImageGrid from './ImageGrid';
import styles from './ImageManager.module.css';
import ImageUploader from './ImageUploader';

/**
 * ImageManager Component
 *
 * Complete image management interface for the admin panel.
 * Provides upload, view, edit, and delete functionality for portfolio images.
 *
 * Features:
 * - Image upload via ImageUploader component
 * - Grid display of all images
 * - Category filtering
 * - Image editing (metadata: title, alt, category, order)
 * - Image deletion with confirmation
 * - Multi-select for bulk operations
 * - Responsive design
 */

const ImageManager: React.FC = () => {
  // State management
  const [images, setImages] = useState<ReadonlyArray<Image>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState<Image | null>(null);
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Multi-select state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<ReadonlySet<string>>(new Set());

  /**
   * Load images from API
   * Fetches all images or filtered by category
   */
  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      let loadedImages: ReadonlyArray<Image>;

      if (selectedCategory === 'all') {
        loadedImages = await api.images.getAll();
      } else {
        const response = await api.images.getImages(selectedCategory, 1, 100);
        loadedImages = response.images;
      }

      setImages(loadedImages);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast.error('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  /**
   * Handle image upload
   * Process multiple files sequentially and refresh image list
   * @param files
   */
  const handleUpload = async (files: ReadonlyArray<File>): Promise<void> => {
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of files) {
        try {
          const categoryToUse = selectedCategory !== 'all' ? selectedCategory : '';
          await api.images.upload(file, categoryToUse || 'uncategorized');
          successCount++;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          errorCount++;
        }
      }

      // Show summary toast
      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded successfully`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} image(s) failed to upload`);
      }

      // Refresh image list
      await loadImages();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle image deletion
   * Shows confirmation dialog and removes image from database and filesystem
   * @param id
   */
  const handleDelete = async (id: string): Promise<void> => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this image? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    try {
      await api.images.delete(id);
      toast.success('Image deleted successfully');
      await loadImages();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image. Please try again.');
    }
  };

  /**
   * Handle edit button click
   * Opens edit modal with selected image
   * @param image
   */
  const handleEdit = (image: Image): void => {
    setEditingImage(image);
  };

  /**
   * Handle save from edit modal
   * Updates image metadata and refreshes list
   * @param updatedData
   */
  const handleSaveEdit = async (updatedData: Partial<Image>): Promise<void> => {
    if (!editingImage) return;

    try {
      await api.images.update(editingImage._id, updatedData);
      toast.success('Image updated successfully');
      setEditingImage(null);
      await loadImages();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update image. Please try again.');
    }
  };

  /**
   * Handle edit modal close
   */
  const handleCloseEdit = (): void => {
    setEditingImage(null);
  };

  /**
   * Toggle selection mode
   */
  const handleToggleSelectionMode = (): void => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      // Clear selection when exiting selection mode
      setSelectedImages(new Set());
    }
  };

  /**
   * Handle image selection toggle
   * @param imageId
   */
  const handleToggleSelection = (imageId: string): void => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  /**
   * Handle select all images
   */
  const handleSelectAll = (): void => {
    if (selectedImages.size === images.length) {
      // Deselect all
      setSelectedImages(new Set());
    } else {
      // Select all
      setSelectedImages(new Set(images.map((img) => img._id)));
    }
  };

  /**
   * Handle bulk category change
   * @param categorySlug
   */
  const handleBulkCategoryChange = async (categorySlug: string): Promise<void> => {
    if (selectedImages.size === 0) return;

    try {
      const result = await api.images.batchUpdate(Array.from(selectedImages), {
        category: categorySlug,
      });
      toast.success(result.message);
      setSelectedImages(new Set());
      setSelectionMode(false);
      await loadImages();
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Failed to update images. Please try again.');
    }
  };

  /**
   * Handle bulk delete
   */
  const handleBulkDelete = async (): Promise<void> => {
    if (selectedImages.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedImages.size} image(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const result = await api.images.batchDelete(Array.from(selectedImages));
      toast.success(result.message);
      setSelectedImages(new Set());
      setSelectionMode(false);
      await loadImages();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete images. Please try again.');
    }
  };

  /**
   * Load categories on mount
   */
  useEffect(() => {
    const loadCategories = async (): Promise<void> => {
      try {
        const response = await api.categories.getAll();
        setCategories(response.categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        toast.error('Failed to load categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  /**
   * Load images on mount and when category changes
   */
  useEffect(() => {
    loadImages();
  }, [loadImages]);

  return (
    <div className={styles.imageManager}>
      {/* Header */}
      <div className={styles.header}>
        <h1>Image Manager</h1>
        <div className={styles.headerActions}>
          <button
            onClick={handleToggleSelectionMode}
            className={`${styles.selectModeButton} ${selectionMode ? styles.active : ''}`}
          >
            {selectionMode ? 'Cancel Selection' : 'Select Multiple'}
          </button>
          <div className={styles.categoryFilter}>
            <label htmlFor="category">Filter by Category:</label>
            <select
              id="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={styles.categorySelect}
              disabled={loadingCategories}
            >
              <option value="all">All Categories</option>
              {loadingCategories ? (
                <option disabled>Loading categories...</option>
              ) : (
                <>
                  {/* Group categories by type */}
                  {['portfolio', 'shop', 'courses'].map((type) => {
                    const typedCategories = categories.filter((cat) => cat.type === type);
                    if (typedCategories.length === 0) return null;

                    return (
                      <optgroup key={type} label={type.charAt(0).toUpperCase() + type.slice(1)}>
                        {typedCategories
                          .filter((cat) => cat.enabled)
                          .sort((a, b) => a.order - b.order)
                          .map((cat) => (
                            <option key={cat._id} value={cat.slug}>
                              {cat.name}
                            </option>
                          ))}
                      </optgroup>
                    );
                  })}
                </>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Action Toolbar */}
      {selectionMode ? (
        <BulkActionToolbar
          selectedCount={selectedImages.size}
          totalCount={images.length}
          categories={categories}
          onSelectAll={handleSelectAll}
          onCategoryChange={handleBulkCategoryChange}
          onDelete={handleBulkDelete}
        />
      ) : null}

      {/* Image Uploader */}
      {!selectionMode && (
        <ImageUploader
          onUpload={handleUpload}
          loading={uploading}
          categoryId={selectedCategory !== 'all' ? selectedCategory : undefined}
        />
      )}

      {/* Image Grid */}
      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading images...</p>
        </div>
      ) : (
        <ImageGrid
          images={images}
          onEdit={handleEdit}
          onDelete={handleDelete}
          selectionMode={selectionMode}
          selectedImages={selectedImages}
          onToggleSelection={handleToggleSelection}
        />
      )}

      {/* Edit Modal */}
      {editingImage ? (
        <ImageEditModal image={editingImage} onSave={handleSaveEdit} onClose={handleCloseEdit} />
      ) : null}
    </div>
  );
};

export default ImageManager;
